// Estado del turno para el "cockpit" compartido (home + hub de MiPAE en turno).
// Helpers puros: fase del turno, timestamps de inicio/fin y formato de cuenta
// regresiva. Centralizados para que el home y el hub hablen el mismo idioma.

import { calcularTurnoDelDia, esPostNoche, addDays, overrideDelDia } from '../utils/calendario';

// Timestamp del inicio del turno de hoy.
export function inicioTurnoTs(turno, base = new Date()) {
  if (!turno || turno.tipo === "libre" || !turno.horaInicio) return null;
  const [h, m] = turno.horaInicio.split(":").map(Number);
  if (!Number.isFinite(h)) return null;
  const d = new Date(base);
  d.setHours(h, m || 0, 0, 0);
  return d.getTime();
}

// Timestamp del fin del turno de hoy (maneja turnos que cruzan medianoche).
export function finTurnoTs(turno, base = new Date()) {
  if (!turno || turno.tipo === "libre" || !turno.horaFin) return null;
  const [h, m] = turno.horaFin.split(":").map(Number);
  if (!Number.isFinite(h)) return null;
  const d = new Date(base);
  d.setHours(h, m || 0, 0, 0);
  if (turno.terminaSiguienteDia) d.setDate(d.getDate() + 1);
  return d.getTime();
}

// Fase del turno según la hora: "antes" → "durante" → "cierre".
// Sin turno (o libre) caemos a "durante": el usuario puede registrar igual.
// `base` es la fecha sobre la que se anclan horaInicio/horaFin (por defecto,
// `now`): existe para poder evaluar un turno que EMPEZÓ AYER (noche/24h) sin
// recalcularlo como si empezara hoy — ver `faseDelTurnoActual`, que es quien
// la usa; el resto de los call-sites no la necesita (mismo comportamiento de
// siempre, 100% retrocompatible).
export function faseDelTurno(turno, now = new Date(), base = now) {
  if (!turno || turno.tipo === "libre" || !turno.horaInicio) return "durante";
  const t = now.getTime();
  const ini = inicioTurnoTs(turno, base);
  const fin = finTurnoTs(turno, base);
  if (ini != null && t < ini) return "antes";
  if (fin != null && t > fin) return "cierre";
  return "durante";
}

// Ventana de gracia tras el horaFin de un turno de noche/24h dentro de la cual
// seguimos anclados a ESE turno para efectos de fase (~14h: cubre a quien
// recién llega de un turno largo/noche sin quedar pegado a "ayer" el resto del
// día). Es una ventana de FASE, no de datos: la retención de los registros es
// aparte y más corta (HORAS_RETENCION = 12 h en registroTurno.js).
const VENTANA_CIERRE_POST_NOCHE_MS = 14 * 3600 * 1000;

// Ancla post-noche: si el usuario viene saliendo (o sigue dentro) de un turno
// de noche/24h que EMPEZÓ AYER, devuelve ese turno con su fecha base — es el
// turno que de verdad rige su momento, aunque la rotación de hoy diga otra
// cosa. Null si no corresponde anclarse. Compartido entre la fase del cierre
// (abajo) y el contexto vivo de Benito (benitoContexto.js), para que ambos
// hablen del MISMO turno con la MISMA ventana.
//
// `overrides` (opcional, Map o Array de turno_overrides) es la lista de días
// que el usuario cambió a mano. Sin ella el ancla mira el patrón puro, que es
// como quedaba antes: si anoche cambiaste tu noche por un libre, la app seguía
// creyendo que saliste de turno. Es opcional para no romper a quien no la
// tiene, pero pásala siempre que la tengas (norma: jamás afirmar un turno que
// el usuario ya cambió).
export function turnoAnclaPostNoche(rotacion, now = new Date(), overrides = null) {
  if (!rotacion || !esPostNoche(now, rotacion, overrides)) return null;
  const ayer = addDays(now, -1);
  const turnoAyer = calcularTurnoDelDia(ayer, rotacion, overrideDelDia(ayer, overrides));
  const finAyer = finTurnoTs(turnoAyer, ayer);
  if (finAyer == null || now.getTime() <= finAyer + VENTANA_CIERRE_POST_NOCHE_MS) {
    return { turno: turnoAyer, base: ayer };
  }
  return null;
}

// Fase "real" del turno, con ancla post-noche. `faseDelTurno` a secas solo
// conoce el turno de HOY según la rotación: si ayer fue un turno de noche o
// de 24h, la rotación de hoy puede ya ser libre/otro tipo, y a las 08:15 del
// día siguiente (recién llegando a casa) la fase nunca devolvía 'cierre' — el
// turno de ayer "ya no existía" para ella, y ResumenCierre/SelloDeTurno no
// aparecían para quien sale de una noche.
//
// Esta envoltura decide, ANTES de usar el turno de hoy, si corresponde
// anclarse al de ayer (post-noche y todavía dentro de la ventana de cierre).
// Deliberadamente NO reemplaza el turno que ven CockpitTurno y compañía (ese
// sigue siendo el de HOY, para no romper su propio countdown/label, que
// asume un turno que empieza hoy) — solo cambia qué FASE gatilla el resumen
// de cierre y el botón "Cerrar turno" en Home.jsx y RegistroTurno.jsx.
//
// `turnoHoy` lo trae el caller (ya lo calculaba para otros usos, como
// CockpitTurno) para no recalcularlo dos veces cuando el ancla no aplica.
export function faseDelTurnoActual(rotacion, turnoHoy, now = new Date(), overrides = null) {
  const ancla = turnoAnclaPostNoche(rotacion, now, overrides);
  if (ancla) return faseDelTurno(ancla.turno, now, ancla.base);
  return faseDelTurno(turnoHoy, now);
}

// ── Momento del turno: captura vs consolidación (E1, propuesta 30-jul) ────────
// La fase horaria (antes/durante/cierre) no distingue el punto en que la
// enfermera deja de CAPTURAR y conviene sentarse a CONSOLIDAR la evolución —
// por eso el riel sugería "Durante" toda la tarde y la evolución se hacía a
// última hora (dolor real del fundador, 31-jul). El corte:
//   corte = max(inicio + 1/2·duración, min(inicio + 2/3·duración, fin − 4 h))
// En un turno largo de 08:00 a 20:00 → 16:00. El piso de la mitad evita que
// en turnos cortos (≤4 h) la resta del fin deje TODO el turno en
// consolidación. Devuelve 'captura' | 'consolidacion' | 'cierre', o null si
// no hay turno con horario (el caller decide su fallback; la ficha usa la
// edad del registro).
export function momentoDelTurno(turno, now = new Date(), base = now) {
  if (!turno || turno.tipo === 'libre' || !turno.horaInicio || !turno.horaFin) return null;
  const fase = faseDelTurno(turno, now, base);
  if (fase === 'cierre') return 'cierre';
  if (fase === 'antes') return 'captura';
  const ini = inicioTurnoTs(turno, base);
  const fin = finTurnoTs(turno, base);
  if (ini == null || fin == null || fin <= ini) return null;
  const dur = fin - ini;
  const corte = Math.max(ini + dur / 2, Math.min(ini + (dur * 2) / 3, fin - 4 * 3600 * 1000));
  return now.getTime() >= corte ? 'consolidacion' : 'captura';
}

// Momento "real", con ancla post-noche (mismo contrato que faseDelTurnoActual:
// quien viene saliendo de una noche/24h sigue regido por ESE turno).
export function momentoDelTurnoActual(rotacion, turnoHoy, now = new Date(), overrides = null) {
  const ancla = turnoAnclaPostNoche(rotacion, now, overrides);
  if (ancla) return momentoDelTurno(ancla.turno, now, ancla.base);
  return momentoDelTurno(turnoHoy, now);
}

// "faltan 1 h 20 min" / "faltan 35 min" a partir de milisegundos restantes.
export function faltaTxt(ms) {
  if (ms == null || ms <= 0) return null;
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

// Timestamp de la próxima ronda (inicio + 2 h), o null si no hay ronda viva.
export function proximaRondaTs(ronda) {
  return ronda?.inicio ? ronda.inicio + 2 * 3600 * 1000 : null;
}

// Gating del chip proactivo de Benito en el hub de MiPAE en turno. Desde el
// 2026-07-18 (decisión del fundador: "que Benito esté siempre, ofreciendo
// ayuda y orientación") el chip se muestra SIEMPRE que el compañero esté
// disponible y haya al menos un paciente; lo que cambia según el contexto es
// el mensaje (con camas pendientes ofrece ayudar con ellas, sin pendientes
// ofrece orientación). Sigue siendo un ofrecimiento sobrio: Benito jamás se
// abre solo. Puro para testear el "cuándo aparece" sin montar RegistroTurno.
export function debeOfrecerBenito({ nPacientes = 0, nConEvolucion = 0, disponible = false } = {}) {
  return !!disponible && nPacientes > 0;
}

// ¿Quedan camas sin evolución? — decide el MENSAJE del chip (ayuda concreta
// vs orientación general), no su visibilidad.
export function benitoConPendientes({ nPacientes = 0, nConEvolucion = 0 } = {}) {
  return nPacientes > 0 && nConEvolucion < nPacientes;
}

export const FASE_LABEL = { antes: "Al partir", durante: "Durante el turno", cierre: "Al cerrar" };
export const FASE_AHORA = { antes: "Por comenzar", durante: "En turno", cierre: "Cerrando turno" };
export const TIPO_TURNO_LABEL = { largo: "Turno largo", noche: "Turno de noche", "24": "Turno de 24h", diurno: "Turno diurno", libre: "Día libre" };
